/**
 * Brand identity for the OAuth deep-link return path (StackedStreak-vn4q).
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * The `appUrlOpen` handler is duplicated, near-verbatim, in TWO entry points:
 *
 *   src/main.tsx       loaded by index.html / echidna.html  — the WEB shells
 *   src/game-main.tsx  loaded by game.html / echidna-game.html — the NATIVE app
 *
 * and vite.config.ts renames the variant shells so that, for a native build,
 * `game.html` becomes the bundle's `index.html`. So the file that actually
 * runs on a phone is game-main.tsx, and main.tsx is the one people read first.
 *
 * That asymmetry already cost a fix: the brand-aware deep-link routing was
 * written into main.tsx alone, which is the entry NATIVE never loads — the
 * change tested clean and would have shipped doing nothing on device. Both
 * copies now import their identity from here, so a brand can't be right in one
 * entry and wrong in the other.
 *
 * The full handlers are NOT unified here. They have genuinely diverged (the
 * native one carries extra Apple-SSO triage logging and a completeness gate),
 * and collapsing them is a refactor that would touch the live Poker Blitz
 * sign-in path for no functional gain. What's shared is what MUST agree.
 */
import { getBrand } from '@/lib/branding';

const IS_ECHIDNA = getBrand().key === 'echidna';

/**
 * The custom scheme the provider redirects back to.
 *
 * Registered per variant, and already in place for both:
 *   iOS      Info.plist / Info-Echidna.plist -> CFBundleURLSchemes
 *   Android  @string/custom_url_scheme, overridden in src/echidna/res/
 */
export const OAUTH_CALLBACK_SCHEME = IS_ECHIDNA
  ? 'com.echidna.game://auth/callback'
  : 'com.pokerblitz.app://auth/callback';

/** The Supabase project host that issues the callback, per app. */
export const OAUTH_PROJECT_HOST = IS_ECHIDNA
  ? 'tqzuvwjglikwybpkisdz.supabase.co'
  : 'irkxrruhnpiadyocnmdt.supabase.co';

/** localStorage key a native auth failure is parked under until the UI reads it. */
export const ECHIDNA_AUTH_ERROR_KEY = 'echidna.authError';

/** Same-tick companion to the key above, for a panel that is already mounted. */
export const ECHIDNA_AUTH_ERROR_EVENT = 'echidna:auth-error';

/**
 * Where to send the player once the deep-link auth flow resolves.
 *
 * The two apps genuinely want different things here, and getting it wrong is
 * not cosmetic:
 *
 *   Poker Blitz  navigates to /auth/callback, which owns profile-completion
 *                routing. Unchanged from the hardcoded behaviour it replaces.
 *
 *   Echidna      does not navigate AT ALL. It has no /auth/callback route, and
 *                it is a single-page game whose account UI is a modal over a
 *                live board — a navigation would 404 and discard the round.
 *                `observeEchidnaUser` is already subscribed to
 *                onAuthStateChange, so establishing the session updates the UI
 *                in place. Errors are parked for useEchidnaAccount to surface,
 *                because there is no landing URL to carry an ?error= param.
 */
export function routeAfterDeepLinkAuth(errorMessage?: string): void {
  if (IS_ECHIDNA) {
    if (!errorMessage) return;
    try {
      window.localStorage.setItem(ECHIDNA_AUTH_ERROR_KEY, errorMessage);
      window.dispatchEvent(
        new CustomEvent(ECHIDNA_AUTH_ERROR_EVENT, { detail: errorMessage }),
      );
    } catch {
      // Storage disabled (private mode). The event above may still have gone
      // out; if neither worked the sheet has still closed, which leaves the
      // player on a playable board rather than stuck behind a dead overlay.
    }
    return;
  }
  window.location.href = errorMessage
    ? `/?error=${encodeURIComponent(errorMessage)}`
    : '/auth/callback';
}
